import {
  type DocumentData,
  type QueryDocumentSnapshot,
  collection,
  firestore,
  onSnapshot,
} from '@ps/functions/firebase/firestore';
import { snapshotToDataWithId } from '@ps/functions/utils/snapshotToDataWithId';
import type { CommunityChannel as CanonicalCommunityChannel } from '@ps/models/lib/community-channel';

import type { CommunityChannel } from '../../types/CommunityChannel';
import { appDateToIso } from '../utils/appDate';

export type ChannelChange =
  | { type: 'added' | 'modified'; channel: CommunityChannel }
  | { type: 'removed'; channelId: string };

/**
 * Subscribe to `stores/{creatorId}/communityChannels` for live
 * add/modify/remove events. The seed list comes from the search
 * endpoint (`fetchChannels`); this listener only emits *changes*
 * after the snapshot first arrives, so callers can merge by id
 * without double-rendering rows.
 *
 * Note: Firestore docs don't carry the full search-shaped channel
 * (no `lastMessage` preview field, for example). What's here is
 * enough to keep the list ordered and the icon/name/visibility
 * fresh — when the design needs the last-message line to update
 * live, the next slice will pull it off the message subscription
 * (creating the message updates the channel's `updatedAt`).
 */
export function subscribeToChannels(
  creatorId: string,
  onChange: (change: ChannelChange) => void,
  onError?: (error: Error) => void,
): () => void {
  const db = firestore();
  if (!db) return () => {};

  const ref = collection(db, 'stores', creatorId, 'communityChannels');

  let firstSnapshot = true;
  const unsubscribe = onSnapshot(
    ref,
    snapshot => {
      // Skip the initial snapshot — the search endpoint already
      // seeded the list, so dispatching `added` for every row would
      // duplicate work and reset scroll state.
      if (firstSnapshot) {
        firstSnapshot = false;
        return;
      }
      for (const change of snapshot.docChanges()) {
        if (change.type === 'removed') {
          onChange({ type: 'removed', channelId: change.doc.id });
          continue;
        }
        const channel = firestoreDocToChannel(change.doc);
        if (!channel) continue;
        onChange({ type: change.type, channel });
      }
    },
    error => onError?.(error),
  );
  return unsubscribe;
}

function firestoreDocToChannel(
  doc: QueryDocumentSnapshot<DocumentData>,
): CommunityChannel | undefined {
  // The channel doc is written server-side in the canonical
  // `CommunityChannel` shape (native `Timestamp`s); cast `DocumentData` once to
  // it, then read typed. Timestamps → ISO for the slice view type via
  // `appDateToIso`; the visibility / subscription-level / lastMessage nominal
  // types differ between the model and the slice view, bridged with a cast.
  const data = snapshotToDataWithId<CanonicalCommunityChannel>(doc);
  if (!data) return undefined;
  return {
    id: data.id,
    slug: data.slug,
    name: data.name,
    description: data.description,
    icon: data.icon,
    visibility: data.visibility as unknown as CommunityChannel['visibility'],
    minimumSubscriptionLevel:
      data.minimumSubscriptionLevel as unknown as CommunityChannel['minimumSubscriptionLevel'],
    isBroadcastChannel: data.isBroadcastChannel ?? false,
    messageCount: data.messageCount,
    memberCount: data.memberCount,
    lastMessage: data.lastMessage as unknown as CommunityChannel['lastMessage'],
    createdAt: appDateToIso(data.createdAt) ?? '',
    updatedAt: appDateToIso(data.updatedAt) ?? '',
  };
}
